'use client'

import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ThemeProvider } from 'next-themes'
import { useState, useEffect, useCallback, useMemo, memo } from 'react'
import { createPortal } from 'react-dom'
import { GlobalTimeTracker } from '@/components/time/global-time-tracker'
import { FloatingTimer } from '@/components/issues/floating-timer'
import { useAuth } from '@/lib/stores/auth-store'
import { useSyncPreferences } from '@/lib/hooks/use-sync-preferences'
import { applyStoredTheme } from './theme-loader'
import { useThemePreferences } from '@/lib/hooks/use-theme-preferences'

// Extend Window interface for theme initialization flag
declare global {
  interface Window {
    __OXIGEN_THEME_INITIALIZED__?: boolean
  }
}

// Wrapper component that only runs authenticated hooks when user is logged in
const AuthenticatedHooks = memo(function AuthenticatedHooks() {
  const { user } = useAuth()
  
  // Always call hooks (required by React rules), but they handle auth check internally
  // The hooks themselves will return early if user is not authenticated
  
  // NOTE: EOD reminders are now handled by Firebase Cloud Function (scheduledEODReminder)
  // No client-side implementation needed

  // Sync preferences from Firebase for cross-device experience
  useSyncPreferences()

  // Ensure theme preferences (and favicon) are applied globally
  useThemePreferences()
  
  return null
})

export function Providers({ children }: { children: React.ReactNode }) {
  // Memoize QueryClient to prevent recreation on every render
  const queryClient = useMemo(() => new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 5 * 60 * 1000, // 5 minutes default - increase from 2
        gcTime: 15 * 60 * 1000, // 15 minutes (formerly cacheTime) - increase from 10
        refetchOnWindowFocus: false, // Disable by default to prevent duplicate requests
        refetchOnMount: false, // Don't refetch on every mount if cache is fresh
        refetchOnReconnect: false, // Don't refetch on reconnect (dashboard doesn't need instant updates)
        retry: 2, // Retry failed requests twice
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000), // Exponential backoff, max 10s
        networkMode: 'online', // Only run queries when online
        // Enable request deduplication
        structuralSharing: true, // Reduces re-renders by sharing unchanged data structures
      },
      mutations: {
        retry: 1, // Retry mutations once
        retryDelay: 1000,
        networkMode: 'online',
      }
    },
  }), [])

  const [timeTrackerOpen, setTimeTrackerOpen] = useState(false)
  const [timeTrackerData, setTimeTrackerData] = useState<{
    issueId?: number
    projectId?: number
    elapsedSeconds?: number
    timeEntryId?: number
    timeEntry?: any
  }>({})
  const [mounted, setMounted] = useState(false)

  // Memoize callbacks to prevent recreating functions on every render
  const handleCloseTimeTracker = useCallback(() => {
    setTimeTrackerOpen(false)
    setTimeTrackerData({})
  }, [])

  const handleTimeTrackerSuccess = useCallback(() => {
    setTimeTrackerOpen(false)
    setTimeTrackerData({})
    window.location.reload()
  }, [])

  useEffect(() => {
    setMounted(true)

    // Mark as hydrated to enable transitions
    document.documentElement.classList.add('hydrated')

    // Only apply if inline script didn't run (SSR fallback)
    if (!window.__OXIGEN_THEME_INITIALIZED__) {
      applyStoredTheme()
    }

    // Timer state is automatically restored from localStorage by Zustand persist
    // Cross-tab sync is handled automatically by Zustand persist middleware
  }, [])

  useEffect(() => {
    const handleOpenTimeTracking = (event: CustomEvent) => {
      setTimeTrackerData({
        issueId: event.detail?.issueId,
        projectId: event.detail?.projectId,
        elapsedSeconds: event.detail?.elapsedSeconds,
        timeEntryId: event.detail?.timeEntryId,
        timeEntry: event.detail?.timeEntry
      })
      setTimeTrackerOpen(true)
    }

    window.addEventListener('openTimeTracking' as any, handleOpenTimeTracking)
    return () => {
      window.removeEventListener('openTimeTracking' as any, handleOpenTimeTracking)
    }
  }, [])

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider
        attribute="class"
        defaultTheme="light"
        enableSystem={false}
        disableTransitionOnChange={true}
        storageKey="theme"
        enableColorScheme={false}
      >
        {/* Only run authenticated hooks when user is logged in */}
        <AuthenticatedHooks />
        
        {children}

        {/* Global Time Tracker - Rendered as portal to document.body */}
        {mounted && createPortal(
          <GlobalTimeTracker
            isOpen={timeTrackerOpen}
            onClose={handleCloseTimeTracker}
            issueId={timeTrackerData.issueId}
            projectId={timeTrackerData.projectId}
            elapsedSeconds={timeTrackerData.elapsedSeconds}
            timeEntryId={timeTrackerData.timeEntryId}
            timeEntry={timeTrackerData.timeEntry}
            onSuccess={handleTimeTrackerSuccess}
          />,
          document.body
        )}

        {/* Floating Timer - Always visible when timer is active */}
        {mounted && createPortal(
          <FloatingTimer />,
          document.body
        )}
      </ThemeProvider>
    </QueryClientProvider>
  )
}